import {
  Box, Typography, Chip, Stack, Slider, TextField,
} from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { ReceiptLong } from "@mui/icons-material";
import { useState, useMemo, useEffect } from "react";
import { usePaginatedQuery, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import { useCycleFilter } from "../cycleFilterContext";
import { DataTableShell } from "../components/table/DataTableShell";
import { CountingFilterSelect } from "../components/table/CountingFilterSelect";
import { DocumentCompareDrawer } from "../components/viewer/DocumentCompareDrawer";
import { AiDisclaimer } from "../components/AiDisclaimer";

const PAGE_SIZE = 250;

function formatCurrency(amount: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);
}

function formatDate(isoDate: string) {
  return new Date(isoDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function confidenceColor(score: number): "success" | "primary" | "warning" | "error" {
  if (score >= 95) return "success";
  if (score >= 80) return "primary";
  if (score >= 50) return "warning";
  return "error";
}

const matchStatusColor: Record<string, "success" | "warning" | "default"> = {
  matched: "success",
  unmatched: "warning",
  excluded: "default",
};

const extractionStatusColor: Record<string, "success" | "warning" | "error" | "default"> = {
  extracted: "success",
  low_confidence: "warning",
  failed: "error",
  pending: "default",
};

const columns: GridColDef[] = [
  {
    field: "vendorName",
    headerName: "Vendor",
    flex: 1.5,
    minWidth: 140,
    sortable: true,
  },
  {
    field: "amount",
    headerName: "Amount",
    width: 120,
    sortable: true,
    align: "right",
    headerAlign: "right",
    renderCell: (params: GridRenderCellParams) => (
      <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(params.value)}</Typography>
    ),
  },
  {
    field: "date",
    headerName: "Date",
    width: 120,
    sortable: true,
    renderCell: (params: GridRenderCellParams) => formatDate(params.value),
  },
  {
    field: "invoiceNumber",
    headerName: "Invoice #",
    width: 130,
    renderCell: (params: GridRenderCellParams) => (
      <Typography variant="body2" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>
        {params.value ?? "-"}
      </Typography>
    ),
  },
  {
    field: "source",
    headerName: "Source",
    width: 130,
    renderCell: (params: GridRenderCellParams) => (
      <Chip size="small" label={String(params.value).replace("_", " ")} variant="outlined" />
    ),
  },
  {
    field: "ocrConfidence",
    headerName: "Confidence",
    width: 130,
    sortable: true,
    renderCell: (params: GridRenderCellParams) => {
      const val = params.value as number;
      return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ width: "100%" }}>
          <Box sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: "var(--c-overlay-6)", overflow: "hidden" }}>
            <Box sx={{
              width: `${Math.min(val, 100)}%`,
              height: "100%",
              borderRadius: 3,
              bgcolor: val >= 80 ? "#00b894" : val >= 50 ? "#fdcb6e" : "#ff7675",
            }} />
          </Box>
          <Typography variant="caption" sx={{ minWidth: 32, textAlign: "right" }}>{val}%</Typography>
        </Stack>
      );
    },
  },
  {
    field: "extractionStatus",
    headerName: "Extraction",
    width: 120,
    renderCell: (params: GridRenderCellParams) => (
      <Chip
        size="small"
        label={String(params.value).replace("_", " ")}
        color={extractionStatusColor[params.value as string] ?? "default"}
      />
    ),
  },
  {
    field: "matchStatus",
    headerName: "Match",
    width: 110,
    renderCell: (params: GridRenderCellParams) => (
      <Chip
        size="small"
        label={params.value}
        color={matchStatusColor[params.value as string] ?? "default"}
      />
    ),
  },
];

export function Invoices() {
  const [sourceFilter, setSourceFilter] = useState("all");
  const [matchFilter, setMatchFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [confidenceRange, setConfidenceRange] = useState<number[]>([0, 100]);
  const [searchTerm, setSearchTerm] = useState("");
  const [compareInvoiceId, setCompareInvoiceId] = useState<string | null>(null);
  const { cycleArg } = useCycleFilter();

  // Resolve invoice + matched bank statement for the row currently selected
  // for compare. Skipped until the user clicks a row.
  const docs = useQuery(
    api.invoices.getDocumentsForCompare,
    compareInvoiceId ? { id: compareInvoiceId as any } : "skip",
  );

  // Client request (3) "view ALL invoices": the old listFiltered query was
  // hard-capped at 500 rows. Paginate server-side (newest first, heavy OCR
  // fields stripped) and keep loading pages until the list is exhausted, so
  // every invoice ends up in the grid while each server call stays bounded.
  const {
    results: data,
    status: pageStatus,
    loadMore,
  } = usePaginatedQuery(
    api.invoices.listPaginated,
    {
      ...(cycleArg as { reconciliationCycleId?: Id<"reconciliationCycles"> }),
      source: sourceFilter === "all" ? undefined : (sourceFilter as any),
      matchStatus: matchFilter === "all" ? undefined : (matchFilter as any),
    },
    { initialNumItems: PAGE_SIZE },
  );
  useEffect(() => {
    if (pageStatus === "CanLoadMore") loadMore(PAGE_SIZE);
  }, [pageStatus, loadMore]);

  const loading = pageStatus === "LoadingFirstPage";
  const loadingMore = pageStatus === "LoadingMore" || pageStatus === "CanLoadMore";
  const allRows: any[] = data ?? [];

  // Live counts for the extraction-status dropdown based on the currently
  // loaded data so the dropdown tells the user which statuses exist.
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {
      extracted: 0,
      low_confidence: 0,
      failed: 0,
      pending: 0,
    };
    for (const r of allRows) {
      const s = r.extractionStatus;
      if (s && counts[s] !== undefined) counts[s]++;
    }
    return counts;
  }, [allRows]);

  const filtered = useMemo(() => {
    return allRows.filter((inv: any) => {
      if (statusFilter !== "all" && inv.extractionStatus !== statusFilter) return false;
      if (inv.ocrConfidence < confidenceRange[0] || inv.ocrConfidence > confidenceRange[1]) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (
          !(inv.vendorName?.toLowerCase() ?? "").includes(term) &&
          !(inv.invoiceNumber?.toLowerCase() ?? "").includes(term)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [allRows, statusFilter, confidenceRange, searchTerm]);

  const rows = filtered.map((inv: any) => ({ ...inv, id: inv._id }));

  const resetFilters = () => {
    setSourceFilter("all");
    setMatchFilter("all");
    setStatusFilter("all");
    setConfidenceRange([0, 100]);
    setSearchTerm("");
  };

  return (
    <>
      <AiDisclaimer context="Invoice fields are extracted by AI/OCR and can be misread" />
      <DataTableShell
        title="Invoices"
        subtitle={
          loadingMore
            ? `Collected invoices from email, Amazon, SharePoint, vendor portals, and manual uploads — loading… (${allRows.length} so far)`
            : "Collected invoices from email, Amazon, SharePoint, vendor portals, and manual uploads"
        }
        data-tour="page-title-invoices"
        rows={rows}
        columns={columns}
        loading={loading}
        totalBeforeFilters={allRows.length}
        itemLabel="invoice"
        emptyTitle="No Invoices Found"
        emptyDescription="Invoices will appear as agents collect them from email, Amazon, and other sources."
        emptyIcon={<ReceiptLong sx={{ fontSize: 32, color: "var(--c-link-accent)" }} />}
        onResetFilters={resetFilters}
        gridProps={{
          initialState: {
            pagination: { paginationModel: { pageSize: 25 } },
            sorting: { sortModel: [{ field: "date", sort: "desc" }] },
          },
          onRowClick: (params) =>
            setCompareInvoiceId(String((params.row as any)._id)),
          sx: { "& .MuiDataGrid-row": { cursor: "pointer" } },
        }}
        filterBar={
          <>
            <CountingFilterSelect
              label="Source"
              value={sourceFilter}
              onChange={setSourceFilter}
              allLabel="All Sources"
              options={[
                { value: "email", label: "Email" },
                { value: "amazon", label: "Amazon" },
                { value: "photo", label: "Photo" },
                { value: "vendor_portal", label: "Vendor Portal" },
                { value: "manual_upload", label: "Manual Upload" },
                { value: "teams", label: "Teams" },
                { value: "sharepoint", label: "SharePoint" },
              ]}
            />
            <CountingFilterSelect
              label="Match Status"
              value={matchFilter}
              onChange={setMatchFilter}
              allLabel="All Match Status"
              options={[
                { value: "matched", label: "Matched" },
                { value: "unmatched", label: "Unmatched" },
                { value: "excluded", label: "Excluded" },
              ]}
            />
            <CountingFilterSelect
              label="Extraction"
              value={statusFilter}
              onChange={setStatusFilter}
              allLabel="All Extraction"
              options={[
                { value: "extracted", label: "Extracted", count: statusCounts.extracted },
                { value: "low_confidence", label: "Low Confidence", count: statusCounts.low_confidence },
                { value: "failed", label: "Failed", count: statusCounts.failed },
                { value: "pending", label: "Pending", count: statusCounts.pending },
              ]}
            />
            <TextField
              size="small"
              label="Search"
              placeholder="Vendor or invoice #..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Stack direction="column" spacing={0.5} sx={{ minWidth: 260, flex: 1, maxWidth: 400 }}>
              <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                Confidence: {confidenceRange[0]}% – {confidenceRange[1]}%
              </Typography>
              <Slider
                value={confidenceRange}
                onChange={(_e, newValue) => setConfidenceRange(newValue as number[])}
                valueLabelDisplay="auto"
                min={0}
                max={100}
                size="small"
                sx={{
                  color: "#6c5ce7",
                  "& .MuiSlider-thumb": { width: 14, height: 14 },
                }}
              />
            </Stack>
          </>
        }
      />

      <DocumentCompareDrawer
        open={Boolean(compareInvoiceId)}
        onClose={() => setCompareInvoiceId(null)}
        title="Document Comparison"
        subtitle={(() => {
          const row = compareInvoiceId
            ? allRows.find((i: any) => i._id === compareInvoiceId)
            : null;
          if (!row) return undefined;
          return `${row.vendorName ?? ""}${row.invoiceNumber ? ` · #${row.invoiceNumber}` : ""} — ${formatCurrency(row.amount ?? 0)}`;
        })()}
        left={{
          storageId: docs?.invoiceStorageId ?? null,
          label: docs?.invoiceVendorName
            ? `Invoice · ${docs.invoiceVendorName}${docs.invoiceNumber ? ` #${docs.invoiceNumber}` : ""}`
            : "Invoice",
        }}
        right={{
          storageId: docs?.bankStorageId ?? null,
          label: docs?.bankFileName
            ? `Bank Statement · ${docs.bankFileName}`
            : "Bank Statement",
        }}
        editTarget={
          compareInvoiceId
            ? { type: "invoice", id: compareInvoiceId }
            : null
        }
      />
    </>
  );
}
